
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Award, Zap, Users, Lightbulb } from 'lucide-react';

const AboutPage: React.FC = () => {
  const whyUs = [
    {
      title: '검증된 기술력',
      desc: '정부 사업 수행 및 다수의 수상 이력을 통해 검증받은 전문성',
      icon: <Award className="h-6 w-6 text-btBlue" />
    },
    {
      title: '실용적 접근',
      desc: '복잡한 탄소중립 과정을 간단하고 효율적인 IT 서비스로 구현',
      icon: <Lightbulb className="h-6 w-6 text-btBlue" />
    },
    {
      title: '다양한 고객층',
      desc: '공공기관부터 시민 개인까지 맞춤형 솔루션 제공 가능',
      icon: <Users className="h-6 w-6 text-btBlue" />
    },
    {
      title: '지속적 혁신',
      desc: '현재 운영 중인 3개 플랫폼을 넘어 지속적인 신규 서비스 개발',
      icon: <Zap className="h-6 w-6 text-btBlue" />
    }
  ];

  return (
    <div className="bg-white">
      {/* Introduction */}
      <section className="py-24 bg-btLightGray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-black text-gray-900 mb-8">
              지속 가능한 미래를 만드는 <br />
              <span className="text-btBlue">기후테크 기업, 블루테라</span>
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              블루테라는 기후위기 시대에 실질적인 탄소중립 실현을 돕는 IT 솔루션 전문 기업입니다.
            </p>
            <div className="flex items-center gap-4 p-6 bg-white rounded-xl border-l-4 border-btBlue shadow-sm">
              <div className="text-btBlue font-black text-2xl">Blue + Terra</div>
              <div className="text-gray-600 font-medium">청정(Blue) + 지구(Terra): 깨끗한 지구를 위한 우리의 약속</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
          <div>
            <h2 className="text-3xl font-black text-gray-900 mb-8 underline decoration-btOrange decoration-4 underline-offset-8">우리의 미션</h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              "모든 이해관계자가 쉽고 효율적으로 탄소중립에 참여할 수 있는 실용적 플랫폼을 제공합니다."
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="h-6 w-6 text-btGreen flex-shrink-0 mt-1" />
                <p className="text-gray-700">재생에너지 보급 확대를 위한 디지털 플랫폼 개발</p>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle2 className="h-6 w-6 text-btGreen flex-shrink-0 mt-1" />
                <p className="text-gray-700">시민 참여형 기후행동 솔루션 제공</p>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle2 className="h-6 w-6 text-btGreen flex-shrink-0 mt-1" />
                <p className="text-gray-700">수송·에너지 부문 탄소저감 기술 혁신</p>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-btBlue/10 rounded-3xl -rotate-2"></div>
            <img 
              src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=2070&auto=format&fit=crop" 
              alt="Office" 
              className="relative rounded-2xl shadow-2xl z-10"
            />
          </div>
        </div>
      </section>

      {/* Why BlueTerra */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-black text-gray-900 mb-4">Why 블루테라?</h2>
            <p className="text-gray-500 font-medium">블루테라를 선택해야 하는 4가지 이유</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUs.map((item, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.03 }}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
              >
                <div className="mb-4 bg-blue-50 p-3 rounded-lg inline-block">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
